import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  pointerWithin,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { Trash2, Edit2, Check, X, ArrowRight, GripVertical, Users } from "lucide-react";
import type { TeamDefinition, Employee } from "../types";
import { useTeams, useEmployees, useTeamsActions, useEmployeesActions } from "../stores";
import { TEAM_COLORS } from "./teamsStore";
import { AddTeamModal } from "./components";
import { PageContainer, PageHeader, PageEmptyState } from "../components/PageLayout";

interface TeamWithEmployees extends TeamDefinition {
  employees: Employee[];
}

export function TeamsOverview() {
  const { t } = useTranslation();
  const storeTeams = useTeams();
  const employees = useEmployees();
  const { addNewTeam, updateTeam, deleteTeam } = useTeamsActions();
  const { updateEmployee } = useEmployeesActions();

  const [showAddModal, setShowAddModal] = useState(false);
  const [draggedEmployee, setDraggedEmployee] = useState<Employee | null>(null);

  const teams: TeamWithEmployees[] = storeTeams.map((team) => ({
    ...team,
    employees: employees.filter((e) => e.team === team.id),
  }));

  const handleMoveEmployee = (employeeId: string, newTeamId: string) => {
    updateEmployee(employeeId, { team: newTeamId });
  };

  const handleDragStart = (event: DragStartEvent) => {
    const employeeId = event.active.data.current?.employeeId as string | undefined;
    const employee = employeeId ? employees.find((e) => e.id === employeeId) : undefined;
    if (employee) {
      setDraggedEmployee(employee);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggedEmployee(null);

    console.log('[TeamsOverview] DragEnd:', { activeId: active.id, overId: over?.id });

    if (!over) {
      console.log('[TeamsOverview] No drop target');
      return;
    }

    const employeeId = active.data.current?.employeeId as string | undefined;
    const overIdStr = over.id as string;

    // Check if dropping on a team drop zone
    if (!overIdStr.startsWith('team-drop-')) {
      console.log('[TeamsOverview] Dropped on non-team:', overIdStr);
      return;
    }

    const targetTeamId = overIdStr.replace('team-drop-', '');

    if (!employeeId) {
      console.log('[TeamsOverview] No employee ID found');
      return;
    }

    const employee = employees.find((e) => e.id === employeeId);
    if (employee && employee.team !== targetTeamId) {
      console.log('[TeamsOverview] Moving employee:', employeeId, 'to team:', targetTeamId);
      handleMoveEmployee(employeeId, targetTeamId);
    }
  };

  return (
    <DndContext
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <PageContainer wide>
        <PageHeader
          title={t('teams.title')}
          subtitle={t('teams.subtitle', { count: teams.length, employeeCount: employees.length })}
          buttonLabel={t('teams.addButton')}
          onButtonClick={() => setShowAddModal(true)}
        />

        {teams.length === 0 ? (
          <PageEmptyState
            icon={<Users size={20} />}
            title={t('teams.noTeams')}
            description={t('teams.noTeamsDesc')}
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {teams.map((team) => (
              <TeamCard
                key={team.id}
                team={team}
                allTeams={storeTeams}
                onUpdate={(updates) => updateTeam(team.id, updates)}
                onDelete={() => deleteTeam(team.id)}
                onMoveEmployee={handleMoveEmployee}
                isDragActive={draggedEmployee !== null}
              />
            ))}
          </div>
        )}

        <AddTeamModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAdd={addNewTeam}
        />
      </PageContainer>

      <DragOverlay>
        {draggedEmployee && (
          <div
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-medium"
            style={{
              backgroundColor: draggedEmployee.color,
              color: "white",
            }}
          >
            <span className="w-2 h-2 rounded-full bg-white/30" />
            {draggedEmployee.name}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

interface TeamCardProps {
  team: TeamWithEmployees;
  allTeams: TeamDefinition[];
  onUpdate: (updates: Partial<Omit<TeamDefinition, 'id'>>) => void;
  onDelete: () => void;
  onMoveEmployee: (employeeId: string, newTeamId: string) => void;
  isDragActive: boolean;
}

function TeamCard({ team, allTeams, onUpdate, onDelete, onMoveEmployee, isDragActive }: TeamCardProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(team.name);
  const [editColor, setEditColor] = useState(team.color);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { setNodeRef, isOver } = useDroppable({
    id: `team-drop-${team.id}`,
  });

  const handleSave = () => {
    if (editName.trim()) {
      onUpdate({ name: editName.trim(), color: editColor });
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditName(team.name);
    setEditColor(team.color);
    setIsEditing(false);
  };

  const handleDelete = () => {
    onDelete();
    setShowDeleteConfirm(false);
  };

  const otherTeams = allTeams.filter(t => t.id !== team.id);

  return (
    <div
      ref={setNodeRef}
      className={`bg-white rounded border overflow-visible transition-all ${
        isOver
          ? "border-gray-400 bg-gray-50"
          : isDragActive
          ? "border-dashed border-gray-300"
          : "border-gray-200"
      }`}
    >
      <div
        className="px-3 py-2 border-b"
        style={{ borderBottomColor: team.color + "40" }}
      >
        {isEditing ? (
          <div className="space-y-2">
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-gray-400 focus:border-gray-400 text-gray-900 bg-white"
              autoFocus
            />
            <div className="flex flex-wrap gap-1">
              {TEAM_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setEditColor(c)}
                  className={`w-5 h-5 rounded-full transition-all ${
                    editColor === c
                      ? "ring-1 ring-offset-0 ring-gray-500 scale-110"
                      : "hover:scale-105"
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                className="flex items-center gap-1 px-2 py-1 text-xs text-green-700 hover:bg-green-50 rounded"
              >
                <Check size={14} />
                {t('common.save')}
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
              >
                <X size={14} />
                {t('common.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: team.color }}
            />
            <h2 className="text-xs font-medium text-gray-900">{team.name}</h2>
            <span className="ml-auto text-xs text-gray-500">
              {t('boards.employeeCount', { count: team.employees.length })}
            </span>
            <button
              onClick={() => setIsEditing(true)}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              title={t('common.edit')}
            >
              <Edit2 size={14} />
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
              title={t('common.delete')}
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>

      {showDeleteConfirm && (
        <div className="px-3 py-2 bg-red-50 border-b border-red-200">
          <p className="text-xs text-red-800 mb-2">
            {t('teams.deleteConfirm', { name: team.name })}
            {team.employees.length > 0 && (
              <span className="block mt-1 font-medium">
                {t('teams.deleteWarning', { count: team.employees.length })}
              </span>
            )}
          </p>
          <div className="flex gap-1.5">
            <button
              onClick={handleDelete}
              className="text-xs text-red-600 border border-red-200 rounded px-2 py-0.5 hover:bg-red-100"
            >
              {t('teams.yesDelete')}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="text-xs text-gray-600 hover:bg-gray-200 rounded px-2 py-0.5"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      <div className="p-3 min-h-[40px]">
        {team.employees.length === 0 ? (
          <p className={`text-xs italic ${isOver ? "text-gray-600" : "text-gray-400"}`}>
            {isOver ? t('common.dropHere') : t('teams.noEmployees')}
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {team.employees.map((employee) => (
              <DraggableEmployeeChip
                key={employee.id}
                employee={employee}
                otherTeams={otherTeams}
                onMove={(newTeamId) => onMoveEmployee(employee.id, newTeamId)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface DraggableEmployeeChipProps {
  employee: Employee;
  otherTeams: TeamDefinition[];
  onMove: (newTeamId: string) => void;
}

function DraggableEmployeeChip({ employee, otherTeams, onMove }: DraggableEmployeeChipProps) {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [hasDragged, setHasDragged] = useState(false);
  const chipRef = useRef<HTMLDivElement>(null);

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `employee-${employee.id}`,
    data: { employeeId: employee.id },
  });

  // Track if we're dragging to prevent click
  useEffect(() => {
    if (isDragging) {
      setHasDragged(true);
    }
  }, [isDragging]);

  // Calculate menu position when opening
  const openMenu = () => {
    if (hasDragged) {
      setHasDragged(false);
      return;
    }
    if (chipRef.current) {
      const rect = chipRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
    setShowMenu(true);
  };

  // Close menu on click outside
  useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = () => {
      setShowMenu(false);
    };

    // Delay to prevent immediate close
    const timeout = setTimeout(() => {
      document.addEventListener("click", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showMenu]);

  // Close on escape
  useEffect(() => {
    if (!showMenu) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowMenu(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showMenu]);

  const handleMove = (teamId: string) => {
    onMove(teamId);
    setShowMenu(false);
  };

  return (
    <>
      <div
        ref={(node) => {
          setNodeRef(node);
          (chipRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }}
        onClick={openMenu}
        {...listeners}
        {...attributes}
        className={`
          inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-medium
          cursor-grab active:cursor-grabbing select-none transition-all
          ${isDragging ? "opacity-30" : "opacity-100 hover:scale-105"}
          ${showMenu ? "ring-1 ring-gray-400" : ""}
        `}
        style={{
          backgroundColor: employee.color + "20",
          color: employee.color,
          border: `1px solid ${employee.color}40`,
        }}
        title={t('common.dragOrClickToChangeTeam')}
      >
        <GripVertical size={10} className="opacity-50" />
        <span
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: employee.color }}
        />
        {employee.name}
      </div>

      {showMenu && otherTeams.length > 0 && createPortal(
        <div
          className="fixed z-[9999] bg-white rounded border border-gray-200 py-1 min-w-[180px]"
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-1.5 text-xs text-gray-500 font-medium border-b border-gray-100">
            {t('common.moveTo')}
          </div>
          {otherTeams.map((team) => (
            <button
              key={team.id}
              onClick={() => handleMove(team.id)}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: team.color }}
              />
              <span className="flex-1 text-left">{team.name}</span>
              <ArrowRight size={14} className="text-gray-400" />
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
